import { PasswordGatingToggle } from "components/inputs/toggle/PasswordGatingToggle";
import { Toggle } from "components/inputs/toggle/Toggle";
import { StripeConnectWrapper } from "components/stripe/StripeConnectWrapper";
import { useClaimCheck } from "hooks/useClaimCheck";
import { useUserStripeData } from "hooks/useUserStripeData";
import { t } from "i18next";
import { useLocation } from "react-router-dom";
import { GatedContentStatus } from "store/stripeConnectAccount/types";
import styles from "./index.module.scss";
import { Button } from "components/buttons/Button";
import { CreatorProductEntitlementsBindingModelDiscriminator } from "@switcherstudio/switcher-api-client";
import { PricingSelectModal } from "components/modal/PricingSelectModal";
import { useMemo } from "react";

export interface BroadcastDetailsGatingOptionsProps {
    isPasswordGatingEnabled: boolean;
    setIsPasswordGatingEnabled: (enabled: boolean) => void;
    password: string;
    handlePasswordChange: (password: string) => void;
    hasPasswordValidationError?: boolean;
    isEmailGatingEnabled: boolean;
    handleEmailToggleChange: (enabled: boolean) => void;
    creatorProductId: string;
    handleSetCreatorProductId: (creatorProductId: string) => void;
    passName?: string;
    pricingSelectModalOpen?: boolean;
    setPricingSelectModalOpen: (open: boolean) => void;
}

export const BroadcastDetailsGatingOptions = ({
    isPasswordGatingEnabled,
    setIsPasswordGatingEnabled,
    password = "",
    handlePasswordChange,
    hasPasswordValidationError = false,
    isEmailGatingEnabled,
    handleEmailToggleChange,
    creatorProductId,
    handleSetCreatorProductId,
    pricingSelectModalOpen = false,
    setPricingSelectModalOpen
}: BroadcastDetailsGatingOptionsProps) => {
    const { pathname } = useLocation();

    const hasPasswordProtectedContentClaim = useClaimCheck(
        "gatedcontent.password"
    );
    const hasEmailProtectedContentClaim = useClaimCheck("gatedcontent.email");
    const hasGatedContentAccess = useClaimCheck("gatedcontent");

    const {
        accountData,
        productData,
        loading: stripeLoading
    } = useUserStripeData({
        requestImmediately: hasGatedContentAccess,
        includeProducts: true,
        redirectPath: pathname
    });

    const stripeConnected =
        accountData?.gatedContentStatus === GatedContentStatus.READY;
    const userHasPasses = productData?.oneTimeProducts?.length > 0;

    const selectedPass = useMemo(() => {
        return productData?.products.find(
            (product) => product.Id === creatorProductId
        );
    }, [productData, creatorProductId]);

    if (!hasGatedContentAccess) {
        return <></>;
    }

    return (
        <StripeConnectWrapper
            inlineLoading
            additionalLoading={stripeLoading}
            showLinkToManageGatedContentPasses={!userHasPasses}
        >
            <div className={styles["gating-options-container"]}>
                <h6 className={!stripeConnected ? styles["remove-mb"] : ""}>
                    {t("video-details-page:gating-options")}
                </h6>
                {hasPasswordProtectedContentClaim && (
                    <PasswordGatingToggle
                        isEnabled={isPasswordGatingEnabled}
                        setIsEnabled={setIsPasswordGatingEnabled}
                        password={password}
                        setPassword={handlePasswordChange}
                        hasEmailOrPurchaseGating={isEmailGatingEnabled}
                        hasValidationError={hasPasswordValidationError}
                    />
                )}
                {hasEmailProtectedContentClaim && (
                    <Toggle
                        on={isEmailGatingEnabled}
                        label={t("collection-page:toggle-email")}
                        disabled={!!creatorProductId}
                        reverseLayout
                        onToggle={handleEmailToggleChange}
                    />
                )}
                <Button
                    disabled={
                        accountData?.gatedContentStatus !==
                        GatedContentStatus.READY
                    }
                    type="badge"
                    isActive={!!creatorProductId}
                    onClick={() => {
                        setPricingSelectModalOpen(true);
                    }}
                >
                    {selectedPass?.Name ?? "Select Pass"}
                </Button>
            </div>

            <PricingSelectModal
                isOpen={pricingSelectModalOpen}
                setIsOpen={setPricingSelectModalOpen}
                buttonText={t("buttons:done")}
                handleSelect={(creatorProduct) =>
                    handleSetCreatorProductId(creatorProduct?.Id)
                }
                products={productData?.oneTimeProducts}
                selected={creatorProductId}
                discriminator={
                    CreatorProductEntitlementsBindingModelDiscriminator._2
                }
            />
        </StripeConnectWrapper>
    );
};
